import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle, XCircle, Loader2, AlertTriangle, TrendingUp,
  Building2, CreditCard, FileText, RefreshCw, BookOpen, ChevronDown, ChevronUp
} from 'lucide-react';

// ============================================================
// Visual Flag Konfiguration — V2: high_confidence | review | low_confidence
// ============================================================
const FLAG_CONFIG = {
  // V2 Flags (tx_match_log-basiert)
  high_confidence: { label: 'Hohe Konfidenz', bg: 'bg-green-50',  border: 'border-green-300', badge: 'bg-green-100 text-green-800',  icon: '✅' },
  review:          { label: 'Prüfen',          bg: 'bg-yellow-50', border: 'border-yellow-300',badge: 'bg-yellow-100 text-yellow-800', icon: '⚠️' },
  low_confidence:  { label: 'Niedrig',         bg: 'bg-red-50',    border: 'border-red-300',   badge: 'bg-red-100 text-red-800',      icon: '🔴' },
  // V1 Legacy Flags (Fallback)
  exact_match:     { label: 'Exakt',        bg: 'bg-green-50',  border: 'border-green-300', badge: 'bg-green-100 text-green-800',  icon: '✅' },
  partial_payment: { label: 'Teilzahlung',  bg: 'bg-yellow-50', border: 'border-yellow-300',badge: 'bg-yellow-100 text-yellow-800', icon: '⚠️' },
  credit:          { label: 'Guthaben',     bg: 'bg-blue-50',   border: 'border-blue-300',  badge: 'bg-blue-100 text-blue-800',    icon: '💙' },
  authority:       { label: 'Behörde',      bg: 'bg-purple-50', border: 'border-purple-300',badge: 'bg-purple-100 text-purple-800', icon: '🏛️' },
  arrears_payment: { label: 'Rückstand',    bg: 'bg-orange-50', border: 'border-orange-300',badge: 'bg-orange-100 text-orange-800', icon: '📅' },
  standard:        { label: 'Standard',     bg: 'bg-gray-50',   border: 'border-gray-200',  badge: 'bg-gray-100 text-gray-700',    icon: '⬜' },
};

function getFlag(visualFlag) {
  return FLAG_CONFIG[visualFlag] || FLAG_CONFIG.standard;
}

// Technische V2-Schlüssel bleiben im Audit-Protokoll unverändert. In der Oberfläche
// werden sie jedoch konsequent als fachlich verständliche deutsche Bezeichnungen gezeigt.
const MATCH_RESULT_LABELS = {
  hit: 'Zugeordnet',
  matched: 'Zugeordnet',
  needs_review: 'Prüfung erforderlich',
  ambiguous: 'Mehrdeutige Zuordnung',
  unmatched: 'Keine Zuordnung',
};

const MATCH_STAGE_LABELS = {
  iban: 'IBAN-Abgleich',
  name: 'Namensabgleich',
  fuzzy_name: 'Namensähnlichkeit',
  token_match: 'Namensbestandteil-Abgleich',
  third_party_reference_name: 'Drittzahler über Verwendungszweck',
  pair_match: 'Paarzuordnung bei Mehrfachzahlung',
  consistent_partial: 'Konsistente Teilzahlung',
  manual_period_aggregation: 'Manuell bestätigte Teilzahlungszuordnung',
  manual_nk_credit_deduction: 'Miete abzüglich Nebenkostenguthaben',
};

function getMatchResultLabel(value) {
  return MATCH_RESULT_LABELS[value] || 'Zuordnung wird geprüft';
}

function getMatchStageLabel(value) {
  return MATCH_STAGE_LABELS[value] || 'Automatische Zuordnung';
}

// ============================================================
// Hilfsfunktionen
// ============================================================
function formatAmount(val) {
  if (val == null) return '–';
  return parseFloat(val).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function formatDate(val) {
  if (!val) return '–';
  try { return new Date(val).toLocaleDateString('de-DE'); } catch { return val; }
}

function formatIban(iban) {
  if (!iban) return '–';
  return iban.replace(/(.{4})/g, '$1 ').trim();
}

function ScoreBar({ score }) {
  const pct = Math.round((score || 0) * 100);
  const color = pct >= 90 ? 'bg-green-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-bold text-gray-700 w-8 text-right">{pct}%</span>
    </div>
  );
}

// ============================================================
// V2-Daten-Normalisierung: API-Response → ReviewCard-Props
// ============================================================
function normalizeReview(item) {
  // V2-Felder auf ReviewCard-Felder mappen
  return {
    ...item,
    // Sender-Felder: V2 hat counterparty_name/counterparty_iban
    sender_name: item.counterparty_name || item.sender_name || item.counterpart_name || '–',
    sender_iban: item.counterparty_iban || item.sender_iban || null,
    // Tenant-Felder: V2 hat matched_entity (Name-String, kein FK)
    tenant_name: item.matched_entity || item.matched_tenant_name || item.tenant_name || '–',
    unit_name: item.signals?.unit_identifier || item.unit_name || '–',
    match_method_label: getMatchStageLabel(item.match_stage_name),
    match_result_label: getMatchResultLabel(item.match_result),
    object_name: item.account_iban ? `IBAN: ${item.account_iban.slice(-4)}` : (item.object_name || ''),
    // Score: V2 hat confidence, V1 hat confidence_score
    score: item.confidence ?? item.confidence_score ?? 0,
    // Verwendungszweck
    usage: item.reference || item.usage || null,
    // visual_flag: V2 liefert es direkt
    visual_flag: item.visual_flag || 'standard',
    // match_result für Badge
    match_result: item.match_result || null,
    // Kein expected_amount in V2 (kein Mietvertrag-FK)
    expected_amount: item.expected_amount ?? null,
    due_month: item.due_month ?? null,
  };
}

// ============================================================
// ReviewCard – 3-Spalten-Layout
// ============================================================
function ReviewCard({ review, onApprove, onReject, processing }) {
  const [learnIban, setLearnIban] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const flag = getFlag(review.visual_flag);
  const isProc = processing.has(review.id);

  const amountDiff = review.expected_amount != null && review.amount != null
    ? parseFloat(review.amount) - parseFloat(review.expected_amount)
    : null;

  return (
    <div className={`rounded-xl border-2 ${flag.border} ${flag.bg} overflow-hidden transition-all`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-white/60">
        <div className="flex items-center gap-2">
          <span className="text-base">{flag.icon}</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${flag.badge}`}>{flag.label}</span>
          {review.match_result && (
            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {review.match_result_label}
            </span>
          )}
          {review.is_authority && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">🏛️ Behörde</span>
          )}
        </div>
        <button onClick={() => setExpanded(e => !e)} className="text-gray-400 hover:text-gray-600 transition-colors">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* 3-Spalten-Body */}
      <div className="grid grid-cols-3 divide-x divide-gray-200">

        {/* Spalte 1: Bank-Transaktion */}
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            <CreditCard className="h-3 w-3" />
            Bank-Eingang
          </div>
          <div className={`text-2xl font-bold ${parseFloat(review.amount) < 0 ? 'text-red-700' : 'text-gray-900'}`}>
            {formatAmount(review.amount)}
          </div>
          <div className="text-sm text-gray-700 font-medium">{review.sender_name || '–'}</div>
          <div className="text-xs text-gray-500 font-mono">{formatIban(review.sender_iban)}</div>
          <div className="text-xs text-gray-500">{formatDate(review.booking_date)}</div>
          {expanded && (
            <div className="mt-2 p-2 bg-white/70 rounded text-xs text-gray-600 italic border border-gray-200">
              <span className="font-semibold not-italic text-gray-700">Verwendungszweck:</span><br />
              {review.usage || '–'}
            </div>
          )}
        </div>

        {/* Spalte 2: Matching */}
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            <TrendingUp className="h-3 w-3" />
            Matching
          </div>
          <ScoreBar score={review.score} />
          <div className="text-sm font-semibold text-blue-800">{review.tenant_name || '–'}</div>
          <div className="text-xs text-gray-500">{review.unit_name} · {review.match_method_label} · {review.object_name}</div>
          {expanded && review.signals && (
            <div className="mt-2 p-2 bg-white/70 rounded text-xs text-gray-600 border border-gray-200">
              <span className="font-semibold text-gray-700">Technische Signale:</span>{' '}
              {Array.isArray(review.signals)
                ? review.signals.join(', ')
                : typeof review.signals === 'object'
                  ? Object.entries(review.signals).map(([k, v]) => `${k}: ${v}`).join(', ')
                  : String(review.signals)}
            </div>
          )}

          {/* IBAN lernen Checkbox */}
          {review.sender_iban && (
            <label className="flex items-center gap-2 mt-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={learnIban}
                onChange={e => setLearnIban(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-600 group-hover:text-gray-800">
                <BookOpen className="h-3 w-3 inline mr-1" />
                IBAN für Mieter merken
              </span>
            </label>
          )}
        </div>

        {/* Spalte 3: Forderung / Aktionen */}
        <div className="p-4 space-y-2">
          <div className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            <FileText className="h-3 w-3" />
            Forderung
          </div>
          {review.expected_amount != null ? (
            <>
              <div className="text-2xl font-bold text-gray-700">{formatAmount(review.expected_amount)}</div>
              <div className="text-xs text-gray-500">{review.due_month || '–'}</div>
              {amountDiff !== null && (
                <div className={`text-sm font-semibold mt-1 ${Math.abs(amountDiff) <= 5 ? 'text-green-600' : amountDiff < 0 ? 'text-red-600' : 'text-blue-600'}`}>
                  {amountDiff >= 0 ? '+' : ''}{formatAmount(amountDiff)}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-gray-400 italic">Kein Mietvertrag verknüpft</div>
          )}

          {/* Aktions-Buttons */}
          <div className="flex flex-col gap-2 mt-4">
            <Button
              onClick={() => onApprove(review, learnIban)}
              disabled={isProc}
              size="sm"
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              {isProc ? (
                <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Verarbeite...</>
              ) : (
                <><CheckCircle className="h-3 w-3 mr-1" />Bestätigen</>
              )}
            </Button>
            <Button
              onClick={() => onReject(review)}
              disabled={isProc}
              size="sm"
              variant="outline"
              className="w-full border-red-300 text-red-600 hover:bg-red-50"
            >
              <XCircle className="h-3 w-3 mr-1" />
              Ablehnen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Haupt-Komponente — V2
// ============================================================
const TransactionReviewQueue = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(new Set());
  const [totalCount, setTotalCount] = useState(0);
  const [successMsg, setSuccessMsg] = useState(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [countRes, dataRes] = await Promise.all([
        fetch('/api/matching/review/count?v=2&scope=general'),
        fetch('/api/matching/review?v=2&scope=general&limit=50')
      ]);
      if (!countRes.ok || !dataRes.ok) throw new Error('API-Fehler');
      const countData = await countRes.json();
      const listData = await dataRes.json();
      setTotalCount(countData.count || 0);
      // Normalisierung: V2-Felder auf ReviewCard-Props mappen
      setReviews((listData.items || []).map(normalizeReview));
    } catch (err) {
      setError('Fehler beim Laden der Review-Queue: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const handleApprove = async (review, learnIban) => {
    setProcessing(prev => new Set([...prev, review.id]));
    try {
      const res = await fetch('/api/matching/review/approve?v=2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tx_id: review.tx_id,
          approved_tenant_name: review.matched_entity || review.tenant_name,
          approved_payment_type: 'kaltmiete',
          reviewed_by: 'clara360',
          note: learnIban && review.sender_iban ? `IBAN gelernt: ${review.sender_iban}` : null,
        })
      });
      if (!res.ok) throw new Error(await res.text());
      setReviews(prev => prev.filter(r => r.id !== review.id));
      setTotalCount(c => Math.max(0, c - 1));
      setSuccessMsg(`✅ Bestätigt${learnIban && review.sender_iban ? ` · IBAN ${review.sender_iban.slice(-4)} gelernt` : ''}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      alert('Fehler beim Bestätigen: ' + err.message);
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(review.id); return s; });
    }
  };

  const handleReject = async (review) => {
    setProcessing(prev => new Set([...prev, review.id]));
    try {
      const res = await fetch('/api/matching/review/reject?v=2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tx_id: review.tx_id,
          reviewed_by: 'clara360',
          note: 'Manuell abgelehnt',
        })
      });
      if (!res.ok) throw new Error(await res.text());
      setReviews(prev => prev.filter(r => r.id !== review.id));
      setTotalCount(c => Math.max(0, c - 1));
    } catch (err) {
      alert('Fehler beim Ablehnen: ' + err.message);
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(review.id); return s; });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Lade Review-Queue...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Allgemeiner Smart Review</h2>
          <p className="text-gray-600 mt-1">
            <span className="font-semibold text-blue-700">{reviews.length}</span> nicht zuordenbare Vorschläge angezeigt
            {totalCount > reviews.length && (
              <span className="text-gray-400 ml-1">(von {totalCount.toLocaleString('de-DE')} gesamt)</span>
            )}
            <span className="ml-2 text-xs text-gray-400 font-mono">v2 · nur ohne belastbare Mieter-/Einheitenbindung</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={loadQueue} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-1" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Erfolgs-Toast */}
      {successMsg && (
        <div className="fixed bottom-6 right-6 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-fade-in">
          {successMsg}
        </div>
      )}

      {/* Legende */}
      <div className="flex flex-wrap gap-2 text-xs">
        {['high_confidence', 'review', 'low_confidence'].map(key => {
          const cfg = FLAG_CONFIG[key];
          return (
            <span key={key} className={`px-2 py-1 rounded-full ${cfg.badge}`}>
              {cfg.icon} {cfg.label}
            </span>
          );
        })}
      </div>

      {/* Karten */}
      {reviews.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Keine allgemeinen Reviews</h3>
            <p className="text-gray-600 mt-2">Nicht zuordenbare Transaktionen sind aktuell nicht offen. Mieterbezogene Prüfungen finden Sie im jeweiligen Mietverlauf.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map(r => (
            <ReviewCard
              key={r.id}
              review={r}
              onApprove={handleApprove}
              onReject={handleReject}
              processing={processing}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TransactionReviewQueue;
